import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc, setDoc, deleteDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { deleteUser } from 'firebase/auth';
import { db, storage, auth } from '../../firebase/config';
import { formatKRPhone } from '../../services/recoveryService';
import { compressImage } from '../../utils/imageUtils';
import { isInRnWebView } from '../../bridge/webviewBridge';
import {
  IoPersonCircleOutline, IoLogOutOutline, IoPeopleOutline,
  IoChevronForward, IoDocumentTextOutline, IoShieldCheckmarkOutline, IoLocationOutline,
  IoConstructOutline, IoInformationCircleOutline, IoCameraOutline, IoCreateOutline, IoCheckmark, IoClose,
  IoTrashOutline, IoWarningOutline
} from 'react-icons/io5';

const isIosWebView = () => {
  return isInRnWebView() && /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const COLORS = {
  primary: '#4A6741',
  active: '#E85830',
  header: '#2C3640',
  background: '#F4F3F8',
};

const MyPage = () => {
  const { userData, logout, refreshUser } = useAuth();
  const { isAdmin, isGeneral } = useRole();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [appVersion, setAppVersion] = useState('');
  const [companyInfo, setCompanyInfo] = useState({
    name: '(주)조군산업 JOGUN INDUSTRY',
    address: '인천광역시 서구 가좌동 524-11',
    phone: '1544-5023',
  });
  const fileInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState('');

  useEffect(() => {
    if (isAdmin) {
      loadPending();
    }
    loadVersion();
    loadCompanyInfo();
  }, [isAdmin]);

  const loadPending = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'users'), where('status', '==', 'pending')));
      setPendingCount(snap.size);
    } catch {}
  };

  const loadCompanyInfo = async () => {
    try {
      const snap = await getDoc(doc(db, 'settings', 'companyInfo'));
      if (snap.exists()) setCompanyInfo(prev => ({ ...prev, ...snap.data() }));
    } catch {}
  };

  const loadVersion = async () => {
    try {
      const q = query(collection(db, 'app_updates'), orderBy('createdAt', 'desc'), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) setAppVersion(snap.docs[0].data().version || '');
    } catch {}
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userData?.uid) return;
    setAvatarUploading(true);
    try {
      const compressed = await compressImage(file, 350, 0.65);
      const storageRef = ref(storage, `avatars/${userData.uid}.webp`);
      await uploadBytes(storageRef, compressed);
      const url = await getDownloadURL(storageRef);
      await setDoc(doc(db, 'users', userData.uid), { avatarUrl: url, updatedAt: serverTimestamp() }, { merge: true });
      await refreshUser();
    } catch (err) {
      console.error('아바타 업로드 실패:', err);
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  };

  const handleNameEdit = () => {
    setNameInput(userData?.name || '');
    setEditingName(true);
  };

  const handleNameSave = async () => {
    if (!nameInput.trim() || !userData?.uid) return;
    setNameSaving(true);
    try {
      await setDoc(doc(db, 'users', userData.uid), { name: nameInput.trim(), updatedAt: serverTimestamp() }, { merge: true });
      await refreshUser();
      setEditingName(false);
    } catch (err) {
      console.error('대화명 변경 실패:', err);
    } finally {
      setNameSaving(false);
    }
  };

  const tryDeleteByField = async (collName, field, value) => {
    try {
      const snap = await getDocs(query(collection(db, collName), where(field, '==', value)));
      if (snap.empty) return;
      const batch = writeBatch(db);
      snap.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    } catch (err) { console.warn(`${collName} 삭제 실패`, err); }
  };

  const handleDeleteAccount = async () => {
    if (!userData?.uid) return;
    setDeleting(true);
    try {
      setDeleteProgress('주문/일정/시공 데이터 삭제 중...');
      await tryDeleteByField('orders', 'contractorUid', userData.uid);
      await tryDeleteByField('schedules', 'assignedTo', userData.uid);
      await tryDeleteByField('constructions', 'contractorUid', userData.uid);
      await tryDeleteByField('posts', 'authorUid', userData.uid);

      setDeleteProgress('알림 토큰 삭제 중...');
      await tryDeleteByField('fcmTokens', 'uid', userData.uid);

      setDeleteProgress('전화번호 인덱스 삭제 중...');
      if (userData.phoneE164) {
        try { await deleteDoc(doc(db, 'phones', userData.phoneE164)); } catch {}
        try { await deleteDoc(doc(db, 'users_by_phone', userData.phoneE164)); } catch {}
      }

      setDeleteProgress('회원 정보 삭제 중...');
      await deleteDoc(doc(db, 'users', userData.uid));

      setDeleteProgress('계정 삭제 중...');
      try {
        if (auth.currentUser) await deleteUser(auth.currentUser);
      } catch (err) {
        console.warn('Auth 계정 삭제 실패 (재로그인 필요할 수 있음):', err);
      }

      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('회원 탈퇴 실패:', err);
      alert('회원 탈퇴 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeleteProgress('');
      setDeleteConfirmText('');
    }
  };

  const handleLogout = async () => {
    if (isIosWebView()) {
      await logout();
      navigate('/login', { replace: true });
      return;
    }
    if (window.confirm('로그인 화면으로 이동하시겠습니까?')) {
      await logout();
      navigate('/login', { replace: true });
    }
  };

  return (
    <Container>

      <ProfileArea>
        <AvatarWrap onClick={handleAvatarClick}>
          {userData?.avatarUrl ? (
            <AvatarImg src={userData.avatarUrl} alt="avatar" />
          ) : (
            <IoPersonCircleOutline size={52} color="#8C9AA4" />
          )}
          <CameraBadge>
            {avatarUploading ? <AvatarSpinner /> : <IoCameraOutline size={14} />}
          </CameraBadge>
          <HiddenInput ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} />
        </AvatarWrap>
        <ProfileInfo>
          <UserName>{userData?.companyName || '업체명 미등록'}</UserName>
          {userData?.representative && <SubName>{userData.representative} 대표</SubName>}
        </ProfileInfo>
        <RoleBadge $admin={isAdmin}>{isAdmin ? '본사 관리자' : isGeneral ? '일반' : '파트너'}</RoleBadge>
      </ProfileArea>

      <InfoSection>
        <InfoItem>
          <InfoLabel>대화명</InfoLabel>
          {editingName ? (
            <NameEditRow>
              <NameEditInput
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                autoFocus
                maxLength={20}
                onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
              />
              <NameEditBtn onClick={handleNameSave} disabled={nameSaving}>
                <IoCheckmark size={18} color={COLORS.primary} />
              </NameEditBtn>
              <NameEditBtn onClick={() => setEditingName(false)}>
                <IoClose size={18} color="#999" />
              </NameEditBtn>
            </NameEditRow>
          ) : (
            <InfoValueRow onClick={handleNameEdit}>
              <InfoValue>{userData?.name || '-'}</InfoValue>
              <IoCreateOutline size={16} color="#B0B8C0" />
            </InfoValueRow>
          )}
        </InfoItem>
        <InfoItem>
          <InfoLabel>전화번호</InfoLabel>
          <InfoValue>{userData?.phoneE164 ? formatKRPhone(userData.phoneE164) : '-'}</InfoValue>
        </InfoItem>
        {userData?.region && (
          <InfoItem>
            <InfoLabel>지역</InfoLabel>
            <InfoValue>{userData.region}</InfoValue>
          </InfoItem>
        )}
        {userData?.loginId && (
          <InfoItem>
            <InfoLabel>아이디</InfoLabel>
            <InfoValue>{userData.loginId}</InfoValue>
          </InfoItem>
        )}
      </InfoSection>

      {!isGeneral && (
        <MenuSection>
          <MenuItem onClick={() => navigate('/work-history')}>
            <IoConstructOutline size={20} />
            <MenuText>시공내역</MenuText>
            <IoChevronForward size={18} color="#ccc" />
          </MenuItem>
        </MenuSection>
      )}

      {isAdmin && (
        <MenuSection>
          <SectionLabel>관리자 메뉴</SectionLabel>
          <MenuItem onClick={() => navigate('/admin/users')}>
            <IoPeopleOutline size={20} />
            <MenuText>회원 관리</MenuText>
            {pendingCount > 0 && <PendingBadge>{pendingCount}</PendingBadge>}
            <IoChevronForward size={18} color="#ccc" />
          </MenuItem>
        </MenuSection>
      )}

      <MenuSection>
        <SectionLabel>약관 및 정책</SectionLabel>
        <MenuItem onClick={() => navigate('/policy/privacy')}>
          <IoShieldCheckmarkOutline size={20} />
          <MenuText>개인정보 처리방침</MenuText>
          <IoChevronForward size={18} color="#ccc" />
        </MenuItem>
        <MenuItem onClick={() => navigate('/policy/terms')}>
          <IoDocumentTextOutline size={20} />
          <MenuText>이용약관</MenuText>
          <IoChevronForward size={18} color="#ccc" />
        </MenuItem>
        <MenuItem onClick={() => navigate('/policy/location')}>
          <IoLocationOutline size={20} />
          <MenuText>위치기반 서비스 이용약관</MenuText>
          <IoChevronForward size={18} color="#ccc" />
        </MenuItem>
      </MenuSection>

      <MenuSection>
        <MenuItem onClick={handleLogout}>
          <IoLogOutOutline size={20} color="#EF5350" />
          <MenuText $danger>로그인 화면으로 이동</MenuText>
          <IoChevronForward size={18} color="#ccc" />
        </MenuItem>
        <MenuItem onClick={() => setShowDeleteModal(true)}>
          <IoTrashOutline size={20} color="#999" />
          <MenuText style={{ color: '#999' }}>회원 탈퇴</MenuText>
          <IoChevronForward size={18} color="#ccc" />
        </MenuItem>
      </MenuSection>

      {showDeleteModal && (
        <ModalOverlay onClick={() => !deleting && setShowDeleteModal(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalIconWrap>
              <IoWarningOutline size={32} color="#EF5350" />
            </ModalIconWrap>
            <ModalTitle>회원 탈퇴</ModalTitle>
            <ModalDesc>
              탈퇴 시 계정과 관련된 모든 데이터(주문, 일정, 시공내역, 게시글, 알림 등)가 영구적으로 삭제되며 복구할 수 없습니다.
              {'\n\n'}계속하려면 아래 입력란에 <b>탈퇴</b> 라고 입력해주세요.
            </ModalDesc>
            <ModalInput
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="탈퇴"
              disabled={deleting}
            />
            {deleting && deleteProgress && <ModalProgress>{deleteProgress}</ModalProgress>}
            <ModalBtnRow>
              <ModalCancelBtn onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                취소
              </ModalCancelBtn>
              <ModalDeleteBtn
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText.trim() !== '탈퇴'}
              >
                {deleting ? '처리 중...' : '탈퇴하기'}
              </ModalDeleteBtn>
            </ModalBtnRow>
          </ModalBox>
        </ModalOverlay>
      )}

      {appVersion && (
        <MenuSection>
          <VersionMenuItem>
            <IoInformationCircleOutline size={20} color="#8C9AA4" />
            <MenuText>버전 정보</MenuText>
            <VersionValue>v{appVersion}</VersionValue>
          </VersionMenuItem>
        </MenuSection>
      )}

      <Footer>
        <FooterText>{companyInfo.name}</FooterText>
        <FooterText>{companyInfo.address}</FooterText>
        <FooterText>대표번호 {companyInfo.phone}</FooterText>
      </Footer>
    </Container>
  );
};

export default MyPage;

const Container = styled.div`
  min-height: 100vh;
  background: ${COLORS.background};
  padding-bottom: 100px;
`;

const ProfileArea = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px;
  margin: 12px 16px 0;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const AvatarWrap = styled.div`
  width: 52px;
  height: 52px;
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarImg = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
`;

const CameraBadge = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  border: 1.5px solid #E0E0E0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
`;

const HiddenInput = styled.input`
  display: none;
`;

const AvatarSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #E0E0E0;
  border-top-color: ${COLORS.primary};
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: #1E2A32;
`;

const SubName = styled.p`
  font-size: 13px;
  color: #8C9AA4;
  margin-top: 2px;
`;

const RoleBadge = styled.span`
  padding: 4px 12px;
  border-radius: 16px;
  background: ${({ $admin }) => $admin ? COLORS.header : '#F0F0F0'};
  color: ${({ $admin }) => $admin ? 'white' : '#666'};
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

const InfoSection = styled.div`
  margin: 12px 16px 0;
  background: white;
  border-radius: 16px;
  padding: 4px 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #F0F0EE;
  &:last-child { border-bottom: none; }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #8C9AA4;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #1E2A32;
  font-weight: 500;
`;

const InfoValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const NameEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NameEditInput = styled.input`
  width: 120px;
  padding: 6px 10px;
  border: 1px solid #D0D0D0;
  border-radius: 8px;
  font-size: 14px;
  text-align: right;
  &:focus { border-color: ${COLORS.primary}; outline: none; }
`;

const NameEditBtn = styled.button`
  display: flex;
  align-items: center;
  padding: 4px;
  &:disabled { opacity: 0.5; }
`;

const MenuSection = styled.div`
  margin: 12px 16px 0;
  background: white;
  border-radius: 16px;
  padding: 4px 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const SectionLabel = styled.p`
  font-size: 12px;
  color: #8C9AA4;
  padding: 12px 20px 4px;
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid #F0F0EE;
  color: #333;
  &:last-child { border-bottom: none; }
`;

const MenuText = styled.span`
  flex: 1;
  text-align: left;
  font-size: 15px;
  color: ${({ $danger }) => $danger ? '#EF5350' : '#1E2A32'};
`;

const PendingBadge = styled.span`
  background: ${COLORS.active};
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
`;

const Footer = styled.div`
  text-align: center;
  padding: 32px 24px;
`;

const FooterText = styled.p`
  font-size: 12px;
  color: #bbb;
  line-height: 1.8;
`;
const VersionMenuItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  color: #333;
`;
const VersionValue = styled.span`
  margin-left: auto;
  font-size: 14px;
  color: #8C9AA4;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;
const ModalBox = styled.div`
  background: white;
  border-radius: 18px;
  width: 100%;
  max-width: 340px;
  padding: 24px 22px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const ModalIconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #FFEBEE;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;
const ModalTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: #1E2A32;
  margin-bottom: 8px;
`;
const ModalDesc = styled.p`
  font-size: 13px;
  color: #5C6770;
  text-align: center;
  line-height: 1.55;
  white-space: pre-line;
  margin-bottom: 14px;
  & b { color: #EF5350; }
`;
const ModalInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #D9DDD6;
  border-radius: 10px;
  font-size: 14px;
  text-align: center;
  margin-bottom: 14px;
  &:focus { border-color: #EF5350; outline: none; }
`;
const ModalProgress = styled.p`
  font-size: 12px;
  color: ${COLORS.primary};
  margin-bottom: 10px;
`;
const ModalBtnRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;
const ModalBaseBtn = styled.button`
  flex: 1;
  height: 44px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const ModalCancelBtn = styled(ModalBaseBtn)`
  background: #F4F5F2;
  color: #1E2A32;
`;
const ModalDeleteBtn = styled(ModalBaseBtn)`
  background: #EF5350;
  color: white;
`;
